/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

// eslint-disable-next-line @typescript-eslint/naming-convention
interface ReverseLiveMap {
	[key: string]: { planned: string; actual: string };
}

export interface ITicketAttributeConfig {
	type: string;
	clientWrite?: boolean;
	actualKey?: string;
	plannedKey?: string;
	lastPlannedKey?: string;
	localKeyOverride?: string;
	fbdbOwned?: boolean;
	apiWrite?: boolean;
}
// eslint-disable-next-line @typescript-eslint/naming-convention
interface ActualizableAttribute extends ITicketAttributeConfig {
	actualKey: string;
	lastPlannedKey: string;
	localKeyOverride: string;
}

export interface IJoinKeyMapping {
	key: string;
	updateFunctions: string[];
}

// eslint-disable-next-line @rushstack/typedef-var
export const joinKeyMapping: { [key: string]: IJoinKeyMapping } = {
	roleId: {
		key: 'role',
		updateFunctions: ['magicRoleRecalc'],
	},
	locationId: {
		key: 'location',
		updateFunctions: [],
	},
	requestedByProjectMemberId: {
		key: 'requestedByProjectMember',
		updateFunctions: ['magicMigrationRecalc'],
	},
	requestedByProjectMemberRoleId: {
		key: 'requestedByProjectMemberRole',
		updateFunctions: ['magicRoleRecalc'],
	},
	responsibleProjectMemberId: {
		key: 'responsibleProjectMember',
		updateFunctions: ['magicMigrationRecalc'],
	},
	responsibleProjectMemberRoleId: {
		key: 'responsibleProjectMemberRole',
		updateFunctions: ['magicRoleRecalc'],
	},
	assignedProjectMemberId: {
		key: 'assignedProjectMember',
		updateFunctions: ['magicMigrationRecalc'],
	},
	assignedProjectMemberRoleId: {
		key: 'assignedProjectMemberRole',
		updateFunctions: ['magicRoleRecalc'],
	},
	priorityId: {
		key: 'priority',
		updateFunctions: [],
	},
	ticketExceptionDays: {
		key: 'ticketExceptionDays',
		updateFunctions: ['magicExceptionDaysRecalc'],
	},
};

/**
 * This list is expected to contain all the firebase (database?) enabled keys.
 * type - The datatype of the data. Not currently used for anything, likely will be.
 * clientWrite - Boolean indicating if this data should be written when the client persists the data to the database
 * fbdbOwned - Boolean indicating if a key is _entirely_ owned by fbdb and applies special behavior where the client doesn't even
 *             copy the key back into the database view. TODO - combine this key and clientWrite into a single enum
 * plannedKey - a special key for actual attributes, contains the name of the planned version of this attribute
 * actualKey - a special key for planned attributes, contains the name of the actual version of this attribute
 * localKeyOverride - a special key that represents the local name of the current value of an actual/ planned attribute pair
 * lastPlannedKey - special key for name of the "lastPlanned" key. Exists on both actual and planned (maybe not do this)
 */
// eslint-disable-next-line @rushstack/typedef-var
export const baseList: { [key: string]: ITicketAttributeConfig } = {
	activityName: {
		type: 'string',
		clientWrite: true,
	},
	actualCrewSize: {
		type: 'number',
		clientWrite: true,
		plannedKey: 'crewSize',
		lastPlannedKey: 'lastPlannedCrewSize',
		localKeyOverride: 'liveCrewSize',
	},
	actualDurationDays: {
		type: 'number',
		clientWrite: true,
		plannedKey: 'durationDays',
		lastPlannedKey: 'lastPlannedDurationDays',
		localKeyOverride: 'liveDurationDays',
	},
	actualDurationDaysInferred: {
		type: 'number',
		clientWrite: false,
	},
	actualDurationHours: {
		type: 'string',
		clientWrite: true,
		plannedKey: 'durationHours',
		lastPlannedKey: 'lastPlannedDurationHours',
		localKeyOverride: 'liveDurationHours',
	},
	actualFinish: {
		type: 'date',
		clientWrite: true,
		plannedKey: 'plannedFinish',
		lastPlannedKey: 'lastPlannedFinish',
		localKeyOverride: 'liveFinish',
	},
	actualStart: {
		type: 'date',
		clientWrite: true,
		plannedKey: 'plannedStart',
		lastPlannedKey: 'lastPlannedStart',
		localKeyOverride: 'liveStart',
	},
	actualStartInferred: {
		type: 'string',
		clientWrite: false,
	},
	actualWorkingDays: {
		type: 'Array<boolean>', //...sure
		clientWrite: true,
		plannedKey: 'workingDays',
		lastPlannedKey: 'lastPlannedWorkingDays',
		localKeyOverride: 'liveWorkingDays',
	},
	actualWorkingDaysInferred: {
		type: 'Array<boolean>', //...sure
		clientWrite: false,
	},
	actualWorkingPeriods: {
		type: 'Array<object>',
		clientWrite: true,
		plannedKey: 'workingPeriods',
		lastPlannedKey: 'lastPlannedWorkingPeriods',
		localKeyOverride: 'liveWorkingPeriods',
	},
	//this relates to  derived data, under another key, can probably just assume that's hard coded?
	assignedProjectMemberId: {
		type: 'string',
		clientWrite: true,
	},
	assignedProjectMemberRoleId: {
		type: 'string',
		clientWrite: true,
	},
	createdAt: {
		type: 'string',
		clientWrite: true,
	},
	//this could have derived data, don't think we need to bother for now though
	creatorId: {
		type: 'string',
		clientWrite: true,
	},
	crewSize: {
		type: 'number',
		clientWrite: true,
		actualKey: 'actualCrewSize',
		lastPlannedKey: 'lastPlannedCrewSize',
		localKeyOverride: 'liveCrewSize',
	},
	constraintState: {
		type: 'string',
		clientWrite: false,
	},
	dateRequested: {
		type: 'date',
		clientWrite: true,
	},
	//dateRequiredOveride was a legacy client write field that overrode this one
	dateRequired: {
		type: 'date',
	},
	durationDays: {
		type: 'number',
		clientWrite: true,
		actualKey: 'actualDurationDays',
		lastPlannedKey: 'lastPlannedDurationDays',
		localKeyOverride: 'liveDurationDays',
	},
	durationHours: {
		type: 'string',
		clientWrite: true,
		actualKey: 'actualDurationHours',
		lastPlannedKey: 'lastPlannedDurationHours',
		localKeyOverride: 'liveDurationHours',
	},
	hasPredecessorOutOfOrder: {
		type: 'boolean',
		fbdbOwned: true,
	},
	hasScheduleViolations: {
		type: 'boolean',
		fbdbOwned: true,
	},
	height: {
		type: 'number',
		clientWrite: true,
	},
	id: {
		type: 'string',
		clientWrite: true,
	},
	preservedTicketId: {
		type: 'string',
		clientWrite: true,
	},
	isActive: {
		type: 'boolean',
		fbdbOwned: true,
	},
	isBlocked: {
		type: 'boolean',
		fbdbOwned: true,
	},
	isCyclic: {
		type: 'boolean',
		fbdbOwned: true,
	},
	isLateConstraintRequested: {
		type: 'boolean',
		fbdbOwned: true,
	},
	isLateTicketFinish: {
		type: 'boolean',
		fbdbOwned: true,
	},
	isOutOfOrder: {
		type: 'boolean',
		fbdbOwned: true,
	},
	lastPlannedCrewSize: {
		type: 'number',
		clientWrite: true,
	},
	lastPlannedDurationDays: {
		type: 'number',
		clientWrite: true,
	},
	lastPlannedDurationHours: {
		type: 'string',
		clientWrite: true,
	},
	lastPlannedFinish: {
		type: 'date',
		clientWrite: true,
	},
	lastPlannedStart: {
		type: 'date',
		clientWrite: true,
	},
	lastPlannedWorkingDays: {
		type: 'Array<string>',
		clientWrite: true,
	},
	lastPlannedWorkingPeriods: {
		type: 'object',
		clientWrite: true,
	},
	left: {
		type: 'number',
		clientWrite: true,
	},
	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7317
	leftGranular: {
		type: 'number',
		clientWrite: false, //ehhhh?
		fbdbOwned: true,
	},
	leftDay: {
		type: 'number',
		clientWrite: false, //ehhhh?
	},
	// END PLACEHOLDER REPLACE AS PART OF TP-7317
	milestoneType: {
		type: 'string',
		clientWrite: true,
	},
	notes: {
		type: 'string',
		clientWrite: true,
	},
	locationId: {
		type: 'string',
		clientWrite: true,
	},
	plannedFinish: {
		type: 'date',
		clientWrite: true,
		actualKey: 'actualFinish',
		lastPlannedKey: 'lastPlannedFinish',
		localKeyOverride: 'liveFinish',
	},
	plannedStart: {
		type: 'date',
		clientWrite: true,
		actualKey: 'actualStart',
		lastPlannedKey: 'lastPlannedStart',
		localKeyOverride: 'liveStart',
	},
	predecessorOutOfOrderCount: {
		type: 'number',
		fbdbOwned: true,
	},
	predecessors: {
		type: 'object',
		clientWrite: true,
	},
	previousAssignedProjectMembers: {
		type: 'object',
		clientWrite: true,
	},
	priorityId: {
		type: 'string',
		clientWrite: true,
	},
	procoreRfiId: {
		type: 'string',
		clientWrite: true,
	},
	promisePeriodPosition: {
		type: 'string',
		clientWrite: true,
	},
	promisePeriodStack: {
		type: 'object',
		clientWrite: true,
	},
	pullColumn: {
		type: 'number',
		fbdbOwned: true,
	},
	responsibleProjectMemberId: {
		type: 'string',
		clientWrite: true,
	},
	requestedByProjectMemberId: {
		type: 'string',
		clientWrite: true,
	},
	requestedByProjectMemberRoleId: {
		type: 'string',
		clientWrite: true,
	},
	responsibleProjectMemberRoleId: {
		type: 'string',
		clientWrite: true,
	},
	roleId: {
		type: 'string',
		clientWrite: true,
	},
	successors: {
		type: 'object',
		clientWrite: true,
	},
	ticketExceptionDays: {
		type: 'Map<string, boolean>',
		clientWrite: false,
	},
	// TP-626 temp workaround
	ticketExceptionDaysPrevPlan: {
		type: 'Map<string, boolean>',
		clientWrite: false,
	},
	tminusFinish: {
		type: 'number',
		fbdbOwned: true,
	},
	top: {
		type: 'number',
		clientWrite: true,
	},
	type: {
		type: 'string',
		clientWrite: true,
	},
	width: {
		type: 'number',
		clientWrite: true,
	},
	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7317`
	widthGranular: {
		type: 'number',
		clientWrite: false, //ehhhh?
		fbdbOwned: true,
	},
	widthDay: {
		type: 'number',
		clientWrite: false, //ehhhh?
	},
	// END PLACEHOLDER REPLACE AS PART OF TP-7317
	workingDays: {
		type: 'object',
		clientWrite: true,
		actualKey: 'actualWorkingDays',
		lastPlannedKey: 'lastPlannedWorkingDays',
		localKeyOverride: 'liveWorkingDays',
	},
	workingPeriods: {
		type: 'object',
		clientWrite: true,
		actualKey: 'actualWorkingPeriods',
		lastPlannedKey: 'lastPlannedWorkingPeriods',
		localKeyOverride: 'liveWorkingPeriods',
	},
	externalScheduleId: {
		type: 'string',
		clientWrite: true,
	},
	externalScheduleFinishDate: {
		type: 'string',
		clientWrite: false,
	},
	wbsCode: {
		type: 'string',
		clientWrite: true,
	},
	wbsExternalSystemId: {
		type: 'string',
		clientWrite: false,
		apiWrite: true,
	},
	wbsImportId: {
		type: 'string',
		clientWrite: false,
		apiWrite: true,
	},
	//exists for api use, non-writable, serves to indicate a target side, instead of a derived side
	ticketPosition: {
		type: 'string',
		clientWrite: false,
	},
	overwriteTransparency: {
		type: 'number',
		clientWrite: false,
	},
};

// eslint-disable-next-line @rushstack/typedef-var
export const derivedProperties = {
	actualPhase: {
		type: 'string',
	},
	isActualized: {
		type: 'boolean',
	},
	liveRole: {
		type: 'object',
	},
	livePromisePeriodStack: {
		type: 'object',
	},
	liveLastPromise: {
		type: 'object',
	},
	liveAssignedProjectMemberId: {
		type: 'string',
	},
	liveExceptionDays: {
		type: 'Map<string, boolean>',
	},
	needsVariance: {
		type: 'boolean',
	},
	side: {
		type: 'string',
	},
	shortActivityName: {
		type: 'string',
	},
	isPlanArchived: {
		type: 'boolean',
	},
	livePlanGranularity: {
		type: 'string',
	},
};

//setup sublists
export const plannedOnly: { [key: string]: ActualizableAttribute } = {};
export const actualOnly: { [key: string]: ActualizableAttribute } = {};
// eslint-disable-next-line guard-for-in
for (const key in baseList) {
	if (baseList[key].actualKey) {
		plannedOnly[key] = baseList[key] as ActualizableAttribute;
	}
	if (baseList[key].plannedKey) {
		actualOnly[key] = baseList[key] as ActualizableAttribute;
	}
}

// eslint-disable-next-line @rushstack/typedef-var
export const localObjectSummary: { [key: string]: ITicketAttributeConfig } = {};
export const reverseLiveMap: ReverseLiveMap = {};
function addToReverse(liveKey: string, type: 'actual' | 'planned', realKey: string): void {
	if (!reverseLiveMap[liveKey]) {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		reverseLiveMap[liveKey] = {} as any;
	}
	reverseLiveMap[liveKey][type] = realKey;
}

// eslint-disable-next-line guard-for-in
for (const key in baseList) {
	// if(key == "plannedFinish"){ debugger; }
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const baseData: any = baseList[key];

	//TODO - don't set this twice in here, it's silyy
	localObjectSummary[key] = baseData;

	if (baseData.actualKey) {
		localObjectSummary[baseData.localKeyOverride] = baseData;
		addToReverse(baseData.localKeyOverride, 'actual', baseData.actualKey);
	}
	if (baseData.plannedKey) {
		addToReverse(baseData.localKeyOverride, 'planned', baseData.plannedKey);
	}
}
//get joinKeyKeys
Object.keys(joinKeyMapping).forEach(
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	(key) => (localObjectSummary[joinKeyMapping[key].key] = joinKeyMapping[key].key as any)
);
Object.assign(localObjectSummary, derivedProperties);

export const localList: string[] = Object.keys(localObjectSummary);

// console.log('reverseLiveMap', reverseLiveMap);
// console.log('localList', localObjectSummary, baseList);
